import { FunctionComponent, useEffect, useState } from 'react';
import * as yup from "yup";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import SearchableInputsDropdown from '../common/SearchableDropdown';
import MemberAvailabilityDropdown from '../common/MemberAvailabilityDropdown';
import { showError } from '../common/ToastNotification';
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

// Types for task with status
interface TaskWithStatus {
    id: string;
    taskName: string;
    taskDescription?: string;
    status: 'Not Started' | 'In Progress' | 'Completed';
    taskType?: string;
    startDate?: string;
    endDate?: string;
    priority?: number;
    assignedToName?: string;
    isReassigned?: boolean;
}

interface ReplaceMemberPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onReplaceMember: (data: {
        originalMember: { key: string; name: string };
        replacementMember: { key: string; name: string };
        selectedTasks: string[];
    }) => void;
    memberToReplace: { key: string; name: string } | null;
    memberTasks: TaskWithStatus[];
    allProjectMembers: { key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[];
    existingGoalMembers?: { key: string; name: string }[]; // Optional since we now include all project members
}

const ReplaceMemberPopup: FunctionComponent<ReplaceMemberPopupProps> = ({
    isOpen,
    onClose,
    onReplaceMember,
    memberToReplace,
    memberTasks,
    allProjectMembers,
    existingGoalMembers // Now optional since we include all project members
}) => {
    const [selectedReplacementMember, setSelectedReplacementMember] = useState<{ key: string; name: string } | null>(null);
    const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
    const [availableReplacementMembers, setAvailableReplacementMembers] = useState<{ key: string; name: string; isAvailable?: boolean; availableHours?: number; totalHours?: number; utilizationPercentage?: number }[]>([]);

    // Validation schema
    const validationSchema = yup.object().shape({
        replacementMember: yup.string().required('Please select a replacement member'),
    });

    const {
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        reset
    } = useForm<any>({
        resolver: yupResolver(validationSchema),
    });

    // Calculate available replacement members (exclude only the member being replaced)
    useEffect(() => {
        if (memberToReplace && allProjectMembers) {
            const available = allProjectMembers.filter(projectMember => {
                // Exclude only the member being replaced (allow all other project members including existing goal members)
                return projectMember.key !== memberToReplace.key;
            });
            setAvailableReplacementMembers(available);
        }
    }, [memberToReplace, allProjectMembers]);

    // Reset state when popup opens/closes
    useEffect(() => {
        if (isOpen) {
            setSelectedTasks([]);
            setSelectedReplacementMember(null);
            reset();
        }
    }, [isOpen, reset]);

    // Get tasks that are "Not Started" and not "Meeting / Knowledge Transfer" (can be selected)
    const notStartedTasks = memberTasks.filter(task =>
        task.status === 'Not Started' &&
        task.taskType !== 'Meeting / Knowledge Transfer'
    );
    const inProgressTasks = memberTasks.filter(task => task.status === 'In Progress');
    const meetingKnowledgeTransferTasks = memberTasks.filter(task =>
        task.taskType === 'Meeting / Knowledge Transfer'
    );

    const handleSelectReplacementMember = (key: string, name: string) => {
        setSelectedReplacementMember({ key, name });
        setValue('replacementMember', name);
        trigger('replacementMember');
    };

    const handleTaskSelection = (taskId: string, isSelected: boolean) => {
        if (isSelected) {
            setSelectedTasks(prev => [...prev, taskId]);
        } else {
            setSelectedTasks(prev => prev.filter(id => id !== taskId));
        }
    };

    const handleSelectAllTasks = (isSelected: boolean) => {
        if (isSelected) {
            setSelectedTasks(notStartedTasks.map(task => task.id));
        } else {
            setSelectedTasks([]);
        }
    };

    const isAllNotStartedTasksSelected = notStartedTasks.length > 0 &&
        notStartedTasks.every(task => selectedTasks.includes(task.id));

    const onSubmit = () => {
        if (!selectedReplacementMember) {
            showError('Please select a replacement member');
            return;
        }

        if (selectedTasks.length === 0) {
            if (notStartedTasks.length === 0) {
                showError('No tasks available for reassignment. Tasks cannot be reassigned if they are in progress, testing, completed, or are Meeting/Knowledge Transfer tasks.');
            } else {
                showError('Please select at least one task to reassign');
            }
            return;
        }

        if (!memberToReplace) {
            showError('No member selected for replacement');
            return;
        }

        onReplaceMember({
            originalMember: memberToReplace,
            replacementMember: selectedReplacementMember,
            selectedTasks: selectedTasks
        });

        handleClose();
    };

    const handleClose = () => {
        reset();
        setSelectedTasks([]);
        setSelectedReplacementMember(null);
        onClose();
    };

    if (!isOpen || !memberToReplace) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-dark-white-item">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 replace-member-round">
                        <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center replace-member-round-dv">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h4 className="text-xl font-bold dark:text-white">Replace Team Member</h4>
                    </div>
                    <button
                        onClick={handleClose}
                        className="rounded-[100px] bg-crimson right_status_clockout flex flex-row items-center justify-center py-1 px-2 text-sm text-white"
                    >
                        <img src={closeDowpDowniconimg} alt="Close" className="w-6 h-6" />
                    </button>
                </div>

                {/* Member being replaced */}
                <div className="mb-6 px-0 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="flex items-center gap-3 replace-member-round-main p-4">
                        <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-semibold replace-member-round-blg">
                            {memberToReplace.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="grid gap-[3px]">
                            <p className="text-sm text-gray-600 dark:text-gray-400 my-0">Replacing</p>
                            <h5 className="font-semibold text-gray-900 dark:text-white my-0">{memberToReplace.name}</h5>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Replacement Member Selection */}
                    <div className="grid grid-cols-1 mq1400:grid-2 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                        <MemberAvailabilityDropdown
                            label="Select Replacement Member*"
                            initialValue={selectedReplacementMember?.name || ''}
                            placeHolder="Select Members"
                            options={availableReplacementMembers}
                            onSelect={handleSelectReplacementMember}
                            errorMessage={errors?.replacementMember}
                        />
                    </div>

                    {/* Tasks Section */}
                    <div className="grid gap-[4px] ">
                        <div className="flex items-center justify-between">
                            <h5 className="text-lg font-semibold dark:text-white">Select Tasks to Reassign</h5>
                            {notStartedTasks.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => handleSelectAllTasks(!isAllNotStartedTasksSelected)}
                                    className="select-btn-memder-gary"
                                >
                                    {isAllNotStartedTasksSelected ? 'Deselect All Tasks' : 'Select All Tasks'}
                                </button>
                            )}
                        </div>

                        <div className=" max-h-64 overflow-y-auto reassign-blog-dv">
                            {/* Not Started Tasks (Selectable) */}
                            {notStartedTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="flex items-start gap-3 p-3 dark:bg-gray-900 rounded-lg border inner-reassign-blog-item"
                                >
                                    <input
                                        type="checkbox"
                                        id={`task-${task.id}`}
                                        checked={selectedTasks.includes(task.id)}
                                        onChange={(e) => handleTaskSelection(task.id, e.target.checked)}
                                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <div className="flex-1 grid gap-[4px]">
                                        <label
                                            htmlFor={`task-${task.id}`}
                                            className="block text-sm font-medium text-gray-900 dark:text-white cursor-pointer mb-[8px]"
                                        >
                                            {task.taskName}
                                            {task.isReassigned && (
                                                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                                    Reassigned
                                                </span>
                                            )}
                                        </label>
                                        {task.taskDescription && (
                                            <p className="text-xs  dark:text-white my-0 font-bold">
                                                {task.taskDescription}
                                            </p>
                                        )}
                                        {(task.startDate || task.endDate) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 flex gap-2">
                                                {task.startDate && (
                                                    <span>Start: {new Date(task.startDate).toLocaleDateString()}</span>
                                                )}
                                                |
                                                {task.endDate && (
                                                    <span>End: {new Date(task.endDate).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <span className="px-2 py-1 text-xs rounded-[50px] bg-aliceblue border-whitesmoke border-solid border-[1px] box-border flex flex-row items-center justify-center py-1 px-4 rounded">
                                        {task.status}
                                    </span>
                                </div>
                            ))}

                            {/* In Progress Tasks (Non-selectable) */}
                            {inProgressTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="flex items-start gap-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg border opacity-60"
                                >
                                    <input
                                        type="checkbox"
                                        disabled
                                        className="mt-1 h-4 w-4 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <div className="flex-1">
                                        <div className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                                            {task.taskName}
                                        </div>
                                        {task.taskDescription && (
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                {task.taskDescription}
                                            </p>
                                        )}
                                        {(task.startDate || task.endDate) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 flex gap-4">
                                                {task.startDate && (
                                                    <span>Start: {new Date(task.startDate).toLocaleDateString()}</span>
                                                )}
                                                {task.endDate && (
                                                    <span>End: {new Date(task.endDate).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <span className="px-2 py-1 text-xs bg-orange-200 dark:bg-orange-800 text-orange-700 dark:text-orange-300 rounded">
                                        {task.status}
                                    </span>
                                </div>
                            ))}

                            {/* Meeting / Knowledge Transfer Tasks (Non-selectable) */}
                            {meetingKnowledgeTransferTasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="flex items-start gap-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg border opacity-60"
                                >
                                    <input
                                        type="checkbox"
                                        disabled
                                        className="mt-1 h-4 w-4 text-gray-400 border-gray-300 rounded cursor-not-allowed"
                                    />
                                    <div className="flex-1">
                                        <div className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                                            {task.taskName}
                                        </div>
                                        {task.taskDescription && (
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                {task.taskDescription}
                                            </p>
                                        )}
                                        {(task.startDate || task.endDate) && (
                                            <div className="text-xs text-gray-500 dark:text-gray-500 mt-1 flex gap-4">
                                                {task.startDate && (
                                                    <span>Start: {new Date(task.startDate).toLocaleDateString()}</span>
                                                )}
                                                {task.endDate && (
                                                    <span>End: {new Date(task.endDate).toLocaleDateString()}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <span className="px-2 py-1 text-xs bg-purple-200 dark:bg-purple-800 text-purple-700 dark:text-purple-300 rounded">
                                        Meeting/KT
                                    </span>
                                </div>
                            ))}

                            {memberTasks.length === 0 && (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    No tasks assigned to this member
                                </div>
                            )}

                            {memberTasks.length > 0 && notStartedTasks.length === 0 && (
                                <div className="text-center py-4 text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                                    <p className="font-medium">No tasks available for reassignment</p>
                                    <p className="text-sm mt-1">Tasks cannot be reassigned if they are in progress, completed, or are Meeting/Knowledge Transfer tasks.</p>
                                </div>
                            )}
                        </div>

                        {/* Task Selection Counter */}
                        {selectedTasks.length > 0 && (
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                {selectedTasks.length} of {notStartedTasks.length} tasks selected
                            </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-4 border-t">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            <span className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">Cancel</span>
                        </button>
                        <button
                            type="submit"
                            className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                            disabled={selectedTasks.length === 0 || !selectedReplacementMember}
                        >
                            <span className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">  Replace</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ReplaceMemberPopup;